import { useEffect, useMemo, useState } from "react";
import { Button, Card, ErrorState, PageHeader, Skeleton } from "../components/ui";
import {
  ApiRequestError,
  fetchAdminUserDailyCost,
  fetchAdminUserLifetimeCost,
  fetchAdminUsersLifetimeCosts,
} from "../lib/api";
import { trackEvent } from "../lib/analytics";
import type {
  AdminUserDailyCost,
  AdminUserDailyCostResponse,
  AdminUserLifetimeCost,
  AdminUserLifetimeCostsPageResponse,
} from "../types";

const ALL_USERS_LIMIT = 50;

function localTodayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toFiniteNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(toFiniteNumber(value));
}

function formatUsd(totalCostMicros: number, displayUsd?: number | null): string {
  const canonical = toFiniteNumber(totalCostMicros) / 1_000_000;
  const source = Number.isFinite(displayUsd as number) ? toFiniteNumber(displayUsd) : canonical;
  const absolute = Math.abs(source);
  const digits = absolute > 0 && absolute < 0.01 ? 6 : absolute < 1 ? 4 : 2;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(source);
}

function formatTimestamp(value?: string | null): string {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function formatAdminError(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 403) {
      return "Permission denied: requires user-cost-visibility group.";
    }
    if (error.status === 400) {
      return error.message || "Invalid date range.";
    }
    return error.message || fallback;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function SummaryTile({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">{label}</p>
      <p className="pr-display mt-2 text-2xl font-semibold">{value}</p>
      <p className="mt-1 text-xs text-[var(--pr-text-muted)]">{helper}</p>
    </div>
  );
}

function DailyCostChart({ days }: { days: AdminUserDailyCost[] }) {
  const maxMicros = useMemo(
    () => Math.max(0, ...days.map((entry) => toFiniteNumber(entry.total_cost_micros))),
    [days],
  );

  if (days.length === 0) {
    return <p className="text-sm text-[var(--pr-text-muted)]">No day-level records returned for this date range.</p>;
  }

  return (
    <div className="space-y-2">
      {days.map((entry, index) => {
        const micros = toFiniteNumber(entry.total_cost_micros);
        const width = maxMicros <= 0 ? 0 : Math.max(2, Math.round((micros / maxMicros) * 100));
        return (
          <div
            key={`${entry.day}-${index}`}
            className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <p className="font-semibold">{entry.day}</p>
              <p className="text-[var(--pr-text-muted)]">
                {formatUsd(micros, entry.total_cost_usd)} · {formatInteger(toFiniteNumber(entry.total_requests))} requests
              </p>
            </div>
            <div className="mt-2 h-2 rounded-full bg-[color-mix(in_srgb,var(--pr-border)_55%,transparent)]">
              <div
                className="h-2 rounded-full bg-[var(--pr-accent)]"
                style={{ width: `${width}%` }}
                aria-hidden
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AdminUserCostsPage() {
  const [userId, setUserId] = useState("");
  const [startDate, setStartDate] = useState(localTodayIsoDate());
  const [endDate, setEndDate] = useState("");

  const [dailyResult, setDailyResult] = useState<AdminUserDailyCostResponse | null>(null);
  const [lifetimeResult, setLifetimeResult] = useState<AdminUserLifetimeCost | null>(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingLifetime, setLoadingLifetime] = useState(false);
  const [singleUserError, setSingleUserError] = useState<string | null>(null);

  const [allUsersPage, setAllUsersPage] = useState<AdminUserLifetimeCostsPageResponse | null>(null);
  const [allUsersCursor, setAllUsersCursor] = useState<string | null>(null);
  const [previousCursors, setPreviousCursors] = useState<Array<string | null>>([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(true);
  const [allUsersError, setAllUsersError] = useState<string | null>(null);

  const cleanedUserId = userId.trim();

  async function loadAllUsers(cursor: string | null): Promise<void> {
    setLoadingAllUsers(true);
    setAllUsersError(null);
    try {
      const payload = await fetchAdminUsersLifetimeCosts(ALL_USERS_LIMIT, cursor ?? undefined);
      setAllUsersPage(payload);
      setAllUsersCursor(cursor);
    } catch (error) {
      setAllUsersError(formatAdminError(error, "Unable to load all-user lifetime costs."));
    } finally {
      setLoadingAllUsers(false);
    }
  }

  useEffect(() => {
    void loadAllUsers(null);
  }, []);

  async function loadDailyCost(): Promise<void> {
    if (!cleanedUserId) {
      setSingleUserError("Enter a user ID before loading costs.");
      return;
    }
    setLoadingDaily(true);
    setSingleUserError(null);
    try {
      const payload = await fetchAdminUserDailyCost(cleanedUserId, startDate, endDate || undefined);
      setDailyResult(payload);
      trackEvent("admin_daily_cost_loaded", { user_id: cleanedUserId });
    } catch (error) {
      setSingleUserError(formatAdminError(error, "Unable to load user daily costs."));
    } finally {
      setLoadingDaily(false);
    }
  }

  async function loadLifetimeCost(): Promise<void> {
    if (!cleanedUserId) {
      setSingleUserError("Enter a user ID before loading costs.");
      return;
    }
    setLoadingLifetime(true);
    setSingleUserError(null);
    try {
      const payload = await fetchAdminUserLifetimeCost(cleanedUserId);
      setLifetimeResult(payload);
      trackEvent("admin_lifetime_cost_loaded", { user_id: cleanedUserId });
    } catch (error) {
      setSingleUserError(formatAdminError(error, "Unable to load user lifetime cost."));
    } finally {
      setLoadingLifetime(false);
    }
  }

  function loadNextPage(): void {
    const next = allUsersPage?.next_cursor ?? null;
    if (!next) {
      return;
    }
    setPreviousCursors((current) => [...current, allUsersCursor]);
    void loadAllUsers(next);
  }

  function loadPreviousPage(): void {
    if (previousCursors.length === 0) {
      return;
    }
    const nextStack = [...previousCursors];
    const targetCursor = nextStack.pop() ?? null;
    setPreviousCursors(nextStack);
    void loadAllUsers(targetCursor);
  }

  return (
    <div data-testid="admin-user-costs-page" className="space-y-4 pb-4">
      <PageHeader
        title="Admin User Cost Visibility"
        subtitle="Requires Cognito group user-cost-visibility. Costs are calculated from total_cost_micros and shown in USD."
      />

      <Card className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-[var(--pr-text-muted)]">User ID</span>
            <input
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              placeholder="user-123"
              className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
            />
          </label>
          <label className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-[var(--pr-text-muted)]">Start date</span>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
            />
          </label>
          <label className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-[var(--pr-text-muted)]">End date (optional)</span>
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            className="min-h-10 px-4 py-2 text-sm"
            disabled={loadingDaily || !cleanedUserId}
            onClick={() => void loadDailyCost()}
          >
            {loadingDaily ? "Loading daily..." : "Load daily / range"}
          </Button>
          <Button
            variant="secondary"
            className="min-h-10 px-4 py-2 text-sm"
            disabled={loadingLifetime || !cleanedUserId}
            onClick={() => void loadLifetimeCost()}
          >
            {loadingLifetime ? "Loading lifetime..." : "Load lifetime"}
          </Button>
        </div>

        {singleUserError ? (
          <div className="rounded-xl border border-rose-500/35 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">{singleUserError}</div>
        ) : null}
      </Card>

      {lifetimeResult ? (
        <Card className="space-y-3">
          <h2 className="text-base font-semibold">User lifetime summary · {lifetimeResult.user_id}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryTile
              label="Total cost"
              value={formatUsd(toFiniteNumber(lifetimeResult.total_cost_micros), lifetimeResult.total_cost_usd)}
              helper={`${formatInteger(toFiniteNumber(lifetimeResult.total_cost_micros))} micros`}
            />
            <SummaryTile
              label="Prompt tokens"
              value={formatInteger(toFiniteNumber(lifetimeResult.total_prompt_tokens))}
              helper="Total prompt tokens"
            />
            <SummaryTile
              label="Completion tokens"
              value={formatInteger(toFiniteNumber(lifetimeResult.total_completion_tokens))}
              helper="Total completion tokens"
            />
            <SummaryTile
              label="Requests"
              value={formatInteger(toFiniteNumber(lifetimeResult.total_requests))}
              helper={`Updated ${formatTimestamp(lifetimeResult.updated_at)}`}
            />
          </div>
        </Card>
      ) : null}

      {dailyResult ? (
        <Card className="space-y-3">
          <h2 className="text-base font-semibold">
            User daily breakdown · {dailyResult.user_id} · {dailyResult.start_date}
            {dailyResult.end_date && dailyResult.end_date !== dailyResult.start_date ? ` to ${dailyResult.end_date}` : ""}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryTile
              label="Range cost"
              value={formatUsd(toFiniteNumber(dailyResult.total_cost_micros), dailyResult.total_cost_usd)}
              helper={`${formatInteger(toFiniteNumber(dailyResult.total_cost_micros))} micros`}
            />
            <SummaryTile
              label="Prompt tokens"
              value={formatInteger(toFiniteNumber(dailyResult.total_prompt_tokens))}
              helper="Across selected days"
            />
            <SummaryTile
              label="Completion tokens"
              value={formatInteger(toFiniteNumber(dailyResult.total_completion_tokens))}
              helper="Across selected days"
            />
            <SummaryTile
              label="Requests"
              value={formatInteger(toFiniteNumber(dailyResult.total_requests))}
              helper={`${formatInteger((dailyResult.days ?? []).length)} day rows returned`}
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-[var(--pr-text-muted)]">Date-range chart (rendered from returned days list)</p>
            <DailyCostChart days={dailyResult.days ?? []} />
          </div>
        </Card>
      ) : null}

      <Card className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">All users lifetime costs</h2>
            <p className="text-sm text-[var(--pr-text-muted)]">
              Cursor pagination uses next_cursor from API responses.
            </p>
          </div>
          <Button
            variant="secondary"
            className="min-h-10 px-4 py-2 text-sm"
            disabled={loadingAllUsers}
            onClick={() => void loadAllUsers(allUsersCursor)}
          >
            {loadingAllUsers ? "Refreshing..." : "Refresh"}
          </Button>
        </div>

        {loadingAllUsers && !allUsersPage ? (
          <div className="space-y-2">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : allUsersError && !allUsersPage ? (
          <ErrorState message={allUsersError} onRetry={() => void loadAllUsers(allUsersCursor)} />
        ) : (
          <>
            {allUsersError ? (
              <div className="rounded-xl border border-rose-500/35 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">{allUsersError}</div>
            ) : null}
            <div className="overflow-x-auto rounded-xl border border-[var(--pr-border)]">
              <table className="min-w-[880px] w-full text-left text-sm">
                <thead className="bg-[var(--pr-surface-secondary)] text-[var(--pr-text-muted)]">
                  <tr>
                    <th className="px-3 py-2 font-semibold">User ID</th>
                    <th className="px-3 py-2 font-semibold">Total cost (USD)</th>
                    <th className="px-3 py-2 font-semibold">Cost (micros)</th>
                    <th className="px-3 py-2 font-semibold">Prompt tokens</th>
                    <th className="px-3 py-2 font-semibold">Completion tokens</th>
                    <th className="px-3 py-2 font-semibold">Requests</th>
                    <th className="px-3 py-2 font-semibold">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {(allUsersPage?.items ?? []).map((item) => (
                    <tr key={item.user_id} className="border-t border-[var(--pr-border)]">
                      <td className="px-3 py-2 font-semibold">{item.user_id}</td>
                      <td className="px-3 py-2">
                        {formatUsd(toFiniteNumber(item.total_cost_micros), item.total_cost_usd)}
                      </td>
                      <td className="px-3 py-2">{formatInteger(toFiniteNumber(item.total_cost_micros))}</td>
                      <td className="px-3 py-2">{formatInteger(toFiniteNumber(item.total_prompt_tokens))}</td>
                      <td className="px-3 py-2">{formatInteger(toFiniteNumber(item.total_completion_tokens))}</td>
                      <td className="px-3 py-2">{formatInteger(toFiniteNumber(item.total_requests))}</td>
                      <td className="px-3 py-2">{formatTimestamp(item.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(allUsersPage?.items ?? []).length === 0 ? (
              <p className="text-sm text-[var(--pr-text-muted)]">No user lifetime rows returned.</p>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-[var(--pr-text-muted)]">
              <p>
                Returned: {formatInteger(toFiniteNumber(allUsersPage?.returned ?? 0))} of limit{" "}
                {formatInteger(toFiniteNumber(allUsersPage?.limit ?? ALL_USERS_LIMIT))}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  className="min-h-10 px-4 py-2 text-sm"
                  disabled={loadingAllUsers || previousCursors.length === 0}
                  onClick={loadPreviousPage}
                >
                  Previous page
                </Button>
                <Button
                  variant="secondary"
                  className="min-h-10 px-4 py-2 text-sm"
                  disabled={loadingAllUsers || !allUsersPage?.next_cursor}
                  onClick={loadNextPage}
                >
                  Next page
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
